// The codes are a shared vocabulary, so this file maps them and never invents them — an
// unrecognised code still renders a sentence rather than a blank or a raw token, because a refusal
// the user cannot read is worse than one they merely dislike. The gate is FEASIBILITY, not
// AUTHORIZATION (DB was explicit): it never asks who is calling, so nothing here may imply the
// answer was access-checked.

/* * How a refusal should read to the user — it changes the tone, not just the text. */
export type MergeRefusalKind =
  | "limit" // a deliberate v1 limit; may lift later, nothing is wrong
  | "fix" // the user can change the selection and succeed
  | "state" // the fields are busy or already merged; time or another action resolves it
  | "unknown"; // a code this build does not know

export interface MergeRefusal {
  code: string;
  kind: MergeRefusalKind;
  /* * what the GROWER reads — always plain language, never the server's phrasing */
  text: string;
  /* * the server's own words about this selection, for the small print. May be engineer language. */
  detail?: string;
}

const COPY: Record<string, { kind: MergeRefusalKind; text: string }> = {
  // ---- structural / the user can fix the selection --------------------------------------------
  too_few_fields: { kind: "fix", text: "Pick at least two fields to combine." },
  duplicate_fields: {
    kind: "fix",
    text: "The same field is selected more than once.",
  },
  not_found: {
    kind: "fix",
    text: "One of these fields no longer exists. Refresh and try again.",
  },
  soft_deleted: { kind: "fix", text: "One of these fields has been deleted." },
  no_boundary: {
    kind: "fix",
    text: "One of these fields has no boundary yet, so there is nothing to combine. Add its boundary first.",
  },

  // ---- already busy or already done ------------------------------------------------------------
  already_merged: {
    kind: "state",
    text: "One of these fields has already been combined into another field.",
  },
  merge_in_flight: {
    kind: "state",
    text: "One of these fields is already being combined. Wait for that to finish, then try again.",
  },
  not_available: {
    // AUTHORISE-ONLY, and it is THE RACE the pre-flight cannot cover by construction: a source was
    // deleted, merged, or claimed by another merge in the window between the gate and the lock. So
    // the copy must say the outcome — NOTHING was combined — because the user just pressed the
    // button on an irreversible action and needs to know it didn't half-happen.
    kind: "state",
    text: "One of these fields was just changed or claimed by another merge. Nothing was combined — try again.",
  },

  // ---- deliberate v1 limits ---------------------------------------------------------------------
  cross_org: {
    kind: "limit",
    text: "Fields in different organizations can’t be combined.",
  },
  // `cross_farm` is RETIRED, not forgotten — DB migrations 108–111: the farm tier no longer exists
  // so the refusal can never be served.
  multiple_sample_bearing: {
    kind: "limit",
    text:
      "More than one of these fields has soil sample results. Combining them would leave one set of samples " +
      "covering ground it never touched, so it isn’t supported yet.",
  },
  overlapping_sections: {
    kind: "limit",
    text:
      "These fields overlap each other. Combining overlapping ground isn’t supported yet — the shared acres " +
      "would be counted twice.",
  },
  cross_utm_zone: {
    // So the copy must not let the user assume it's the different-farms rule, and must not leave
    // them hunting for a fix that doesn't exist.
    kind: "limit",
    text:
      "These fields sit too far apart east-to-west to share one map grid — which can happen even on the same " +
      "farm. Combining them isn’t supported yet.",
  },
  mixed_build_anchor: {
    kind: "limit",
    text:
      "These fields’ map data was built from different years of source imagery, so their numbers aren’t " +
      "directly comparable yet.",
  },
  anchor_unknown: {
    kind: "limit",
    text: "We can’t confirm these fields’ map data was built on the same basis, so we won’t combine them.",
  },
};

/*
 *
 * Two corrections crossed in flight and would have produced an all-unknown refusal list: every
 * reason rendering as "these fields can't be combined " with the real cause dropped, on the screen
 * for an irreversible action.
 */
export interface MergeGateRow {
  reason?: string | null;
  code?: string | null;
  detail?: string | null;
}

/*
 *
 * It names the specific thing ("sources span UTM zones 15, 16") which is genuinely useful — but it
 * is written for engineers ("the union cannot share one projected lattice"), and concatenating it
 * would undo the plain sentence it follows.
 */
export function mergeRefusal(row: MergeGateRow): MergeRefusal {
  const code = (row.reason ?? row.code ?? "").trim();
  const detail = (row.detail ?? "").trim() || undefined;
  const known = COPY[code];
  if (!known) {
    // An unknown code means the server grew a reason this build doesn't know. Say that plainly
    // rather than rendering a bare token or, worse, nothing at all.
    return {
      code,
      kind: "unknown",
      text:
        detail || `These fields can’t be combined${code ? ` (${code})` : ""}.`,
      detail,
    };
  }
  return { code, kind: known.kind, text: known.text, detail };
}

/* * Every refusal for a selection, in the order the server gave them. Empty = mergeable. */
export function mergeRefusals(
  rows: MergeGateRow[] | null | undefined,
): MergeRefusal[] {
  return (rows ?? []).map(mergeRefusal);
}

/*
 *
 * Zero refusals is the ONLY mergeable answer — an absent or failed check is never a yes. BE-Light
 * also serves an explicit `mergeable` boolean; this reads the REASONS rather than trusting it, so
 * the button and the sentence under it can never disagree.
 */
export const isMergeable = (rows: MergeGateRow[] | null | undefined): boolean =>
  Array.isArray(rows) && rows.length === 0;

/*
 *
 * Returns null — not `` — when the error is something else, so the caller can tell "refused for
 * these reasons" from "failed for an unrelated reason" and never renders an empty refusal list.
 */
export function mergeRefusalsFromError(e: unknown): MergeRefusal[] | null {
  const err = e as { code?: string; details?: MergeGateRow[] } | null;
  if (!err || err.code !== "ERR_MERGE_REFUSED") return null;
  const rows = Array.isArray(err.details) ? err.details : [];
  // A refusal with no rows is still a refusal — fall back to the generic sentence rather than
  // rendering nothing, which would leave the dialog silently unchanged after a click.
  return rows.length ? mergeRefusals(rows) : [mergeRefusal({ reason: "" })];
}
